import { Box, Checkbox, Combobox, FormField, FormHelp, Heading, Label, LabeledControl } from '@zillow/constellation-10';
import { Controller, useFormContext } from 'react-hook-form';
import { PreferredLeaseLength_values } from '@zg-rentals/ts-capps-client';
import { PREFERRED_LEASE_LENGTH_LABELS } from '@zg-rentals/applications-utils';
import { today } from '../../../../utils/formatters';
import { validateDate } from '../../../../utils/validation';
import { FIELD_IDS } from '../../../../constants/wizard';
import { isPreferredLeaseLengthAnswered } from '../../../../constants/validation';
import { useIsBaseApplicationChangesEnabled } from '../../../../utils/ab-tests';
import { DatePicker } from '../../../DatePicker';
import { useMount } from 'ahooks';

const PREFERRED_LEASE_LENGTH_OPTIONS = PreferredLeaseLength_values.map((value) => ({
  value,
  label: PREFERRED_LEASE_LENGTH_LABELS[value],
}));

export function MoveInDateStep() {
  const { getValues, setValue } = useFormContext();
  const isMoveInFlexible = getValues(FIELD_IDS.MOVE_IN_FLEXIBLE);
  const isBaseApplicationChangesEnabled = useIsBaseApplicationChangesEnabled();
  const leaseLengthFlexibleDeps: Array<string> = [FIELD_IDS.PREFERRED_LEASE_LENGTHS];

  useMount(() => {
    if (isMoveInFlexible === null) {
      setValue(FIELD_IDS.MOVE_IN_FLEXIBLE, false);
    }
    if (isBaseApplicationChangesEnabled && getValues(FIELD_IDS.LEASE_LENGTH_FLEXIBLE) == null) {
      setValue(FIELD_IDS.LEASE_LENGTH_FLEXIBLE, false);
    }
  });

  return (
    <>
      <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
        Requested move-in date
      </Heading>
      <Controller
        name={FIELD_IDS.MOVE_IN_DATE}
        rules={{
          required: 'Requested move-in date is required',
          validate: (v) =>
            validateDate(v, {
              inTheFuture: true,
            }),
        }}
        render={({ field, fieldState: { error, invalid } }) => (
          <FormField
            label={<Label id="move-in-date-label">Move-in date</Label>}
            description={error && <FormHelp>{error?.message}</FormHelp>}
            error={invalid}
          >
            <DatePicker {...field} minDate={today()} aria-labelledby="move-in-date-label" />
          </FormField>
        )}
      />
      <Controller
        name={FIELD_IDS.MOVE_IN_FLEXIBLE}
        render={({ field: { onChange, value, onBlur } }) => (
          <LabeledControl
            label={<Label>I’m flexible with my move-in date</Label>}
            control={<Checkbox onChange={onChange} name={FIELD_IDS.MOVE_IN_FLEXIBLE} checked={value} onBlur={onBlur} />}
          />
        )}
      />
      {isBaseApplicationChangesEnabled && (
        <Box css={{ marginTop: 'loose' }}>
          <Controller
            name={FIELD_IDS.PREFERRED_LEASE_LENGTHS}
            rules={{
              validate: (value) =>
                isPreferredLeaseLengthAnswered(value, getValues(FIELD_IDS.LEASE_LENGTH_FLEXIBLE)) ||
                'Enter your preferred lease length',
            }}
            render={({ field: { onChange, onBlur, value }, fieldState: { error, invalid } }) => (
              <FormField
                label={<Label>Preferred lease length</Label>}
                description={<FormHelp>{error?.message ?? 'Select all lease lengths that work for you'}</FormHelp>}
                error={invalid}
                control={
                  <Combobox
                    options={PREFERRED_LEASE_LENGTH_OPTIONS}
                    value={value ?? []}
                    onChange={onChange}
                    onOpenChange={(open) => {
                      if (!open) {
                        onBlur();
                      }
                    }}
                    appearance="select"
                    editable={false}
                    showOptionControl
                    showLabelForValue
                  />
                }
              />
            )}
          />
          <Controller
            name={FIELD_IDS.LEASE_LENGTH_FLEXIBLE}
            rules={{ deps: leaseLengthFlexibleDeps }}
            render={({ field: { onChange, value, onBlur } }) => (
              <LabeledControl
                label={<Label>I’m flexible with lease length</Label>}
                control={
                  <Checkbox
                    onChange={onChange}
                    name={FIELD_IDS.LEASE_LENGTH_FLEXIBLE}
                    checked={value === true}
                    onBlur={onBlur}
                  />
                }
              />
            )}
          />
        </Box>
      )}
    </>
  );
}
